import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import { ChangelogEntryCard } from '@/components/changelog/changelog-entry-card'
import {
    getAllEntries,
    getCustomerEntries,
    getLatestEntryDate,
    loadEntries,
} from '@/lib/changelog/load-entries'
import { setLastSeenAt } from '@/lib/changelog/last-seen'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/whats-new')({
    // Viewing the changelog acknowledges every entry. Done in the loader (runs
    // on navigation, not render) to avoid a mount effect.
    loader: () => {
        const latest = getLatestEntryDate(loadEntries())
        if (latest) setLastSeenAt(latest)
        return null
    },
    component: WhatsNewPage,
})

function WhatsNewPage() {
    const { hasPermission } = useAuth()
    const canSeeInternal = hasPermission('settings.manage')

    const { customer, internal } = useMemo(() => {
        const all = loadEntries()
        return {
            customer: getCustomerEntries(all),
            internal: canSeeInternal
                ? getAllEntries(all).filter((e) => e.audience === 'internal')
                : [],
        }
    }, [canSeeInternal])

    const isEmpty = customer.length === 0 && internal.length === 0

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <header>
                <h1 className="text-xl font-semibold">{m.whats_new_title()}</h1>
                <p className="text-sm text-muted-foreground">
                    {m.whats_new_subtitle()}
                </p>
            </header>

            {isEmpty ? (
                <p className="text-sm text-muted-foreground">
                    {m.whats_new_empty()}
                </p>
            ) : (
                <div className="space-y-3">
                    {customer.map((entry) => (
                        <ChangelogEntryCard key={entry.id} entry={entry} />
                    ))}
                </div>
            )}

            {internal.length > 0 && (
                <section className="space-y-3">
                    <h2 className="text-sm font-semibold text-muted-foreground">
                        {m.whats_new_internal_section()}
                    </h2>
                    {internal.map((entry) => (
                        <ChangelogEntryCard key={entry.id} entry={entry} />
                    ))}
                </section>
            )}
        </div>
    )
}
